import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Waves, Printer, ArrowRight, FlaskConical, Truck, Home as HomeIcon, MonitorSmartphone,
  Database, ShoppingCart, Droplets, CheckCircle2, Circle, Mail, Copy, Check, Phone,
  Beaker, ShieldCheck, TrendingUp, Layers, Sparkles, Building2, Handshake,
} from "lucide-react";
import Logo from "../components/Logo";
import { CONTACT, ROADMAP, TESTER_IMG, COMMERCIAL } from "../data/build";
import { IMG } from "../data/content";
import { OUTREACH } from "../components/shop/OutreachEmail";

const MOBILE_IMG = "https://static.prod-images.emergentagent.com/jobs/a99c2969-7ac0-4b5c-adb1-9cb1c6716fac/images/2ea8dea3d6180e731c9b5f3620368adbe7b4bd903e1fbb2110c9df2b48e9821a.jpeg";

const STATS = [
  { n: "15 yrs", l: "of real trade history" },
  { n: "34,000+", l: "invoices migrated" },
  { n: "38,000+", l: "work orders" },
  { n: "51,000+", l: "quotes" },
  { n: "6,530", l: "pools on file" },
  { n: "8,897", l: "customers" },
];

const PRODUCTS = [
  { icon: FlaskConical, tag: "In-store", name: "Pool Shop Tester", desc: "A reagentless benchtop tester — four continuous probes + three optical strips — a full water panel in ~60–90 seconds, guided by each customer's pool profile.", img: TESTER_IMG.front },
  { icon: Truck, tag: "In the field", name: "Mobile Tester", desc: "The shop tester's core, packaged for the pool technician's van — testing on-site and syncing every result straight back to the one database.", img: MOBILE_IMG },
  { icon: HomeIcon, tag: "In the backyard", name: "Home Test & Dose", desc: "An installed smart unit that tests and doses the pool automatically, keeps the water perfect, and reorders chemistry — with live database access for the owner.", img: IMG.scene },
  { icon: MonitorSmartphone, tag: "The brains", name: "Pool Shop Software", desc: "One live cloud database feeding shop, field and home — testing, POS, jobs, invoicing, loyalty and reporting. Already running the real business.", img: "/proposal/ns-switchboard.png" },
];

const FLOW = [
  { icon: Droplets, t: "Test the water", d: "Guided test against that pool's own saved ranges — chlorine, pH, salt, alkalinity, phosphate, magnesium and more, with BioGuard chemistry built in." },
  { icon: Sparkles, t: "Get the fix", d: "The software reads the results and builds the exact product list to correct the water — no guesswork, no lost sales." },
  { icon: ShoppingCart, t: "One tap to POS", d: "\u201CSell Recommended at POS\u201D drops the whole basket into the till — loyalty discount applied, invoice raised, stock moved." },
  { icon: Database, t: "It all sticks", d: "Every test, sale and job lands on the customer's record forever — building the water history that keeps them coming back." },
];

const LIVE = [
  "Central cloud client database — one source of truth",
  "Guided in-store water testing with per-pool custom ranges",
  "\u201CTest \u2192 recommended products \u2192 POS\u201D in one tap",
  "Full retail POS — loyalty tiers, split payments, refunds, cash drawer",
  "Work orders & technician job cards",
  "Quotes, invoices & statements with 30/60/90 aging",
  "FM84 warranty submissions (Spa Industries)",
  "Recurring service contracts",
  "White-label multi-brand printing (logo, ABN, bank — auto letterheads)",
  "Water-test history per pool with BioGuard ranges",
  "Staff time sheets with payroll export",
  "Reports with one-click XLSX / PDF export",
];

const NEXT = [
  "Live email statement chase for overdue debtors",
  "Sanitiser \u2192 chemical range auto-suggest",
  "Card terminal & tap-to-pay integration",
  "Accounting sync — Xero, MYOB, QuickBooks, Quicken",
  "Home test-and-dose automation (Project 3)",
  "Consumer app with live water-history access",
];

const ASK = [
  { icon: TrendingUp, t: "Strategic investment", d: "Capital to take the platform from a proven, live software base through hardware prototyping to production." },
  { icon: Beaker, t: "Chemistry & brand partner", d: "Bioguard science at the core of the tester and the auto-doser — dosed, tracked and reordered automatically." },
  { icon: Building2, t: "Manufacturing & build", d: "A path to build the benchtop, mobile and home units at pilot then production scale." },
  { icon: Handshake, t: "Distribution", d: "Australia first, then the far larger USA market and beyond — through an established channel." },
];

const Eyebrow = ({ children }) => (
  <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-sky-700">{children}</p>
);
const Section = ({ id, dark, children, className = "" }) => (
  <section id={id} className={`px-5 lg:px-8 py-16 md:py-24 ${dark ? "bg-[#0b1f36] text-white" : ""} ${className}`}>
    <div className="mx-auto max-w-6xl">{children}</div>
  </section>
);
const Shot = ({ src, cap, tag }) => (
  <figure className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-lg">
    <div className="bg-slate-100 border-b border-slate-200 px-3 py-2 flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      {tag && <span className="ml-2 font-mono2 text-[10px] uppercase tracking-widest text-slate-400">{tag}</span>}
    </div>
    <img src={src} alt={cap} className="w-full block" />
    {cap && <figcaption className="px-4 py-3 text-sm text-slate-600 border-t border-slate-100">{cap}</figcaption>}
  </figure>
);

export default function Proposal() {
  const [copied, setCopied] = useState(false);
  const proposalUrl = useMemo(() => (typeof window !== "undefined" ? `${window.location.origin}/proposal` : "/proposal"), []);
  const inviteBody = useMemo(() => OUTREACH.biolab.body.replace("[PROPOSAL_LINK]", proposalUrl), [proposalUrl]);
  const mailto = `mailto:gene.lawless@biolabinc.com?subject=${encodeURIComponent(OUTREACH.biolab.subject)}&body=${encodeURIComponent(inviteBody)}`;

  const copyInvite = async () => {
    const full = `Subject: ${OUTREACH.biolab.subject}\n\n${inviteBody}`;
    try { await navigator.clipboard.writeText(full); }
    catch (e) {
      const ta = document.createElement("textarea"); ta.value = full; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch (err) { /* noop */ } document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="min-h-screen bg-[#FAFCFD] text-slate-900" data-testid="proposal-page" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
      {/* Top bar */}
      <header className="no-print sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline font-mono2 text-[10px] uppercase tracking-widest text-slate-400">Private preview · G. Lawless</span>
            <button onClick={() => window.print()} data-testid="proposal-print-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 transition-colors">
              <Printer className="h-4 w-4" /> Print / PDF
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50 via-white to-white" />
        <div className="absolute -top-24 -right-24 -z-10 h-[420px] w-[420px] rounded-full bg-sky-200/40 blur-3xl" />
        <div className="mx-auto max-w-6xl px-5 lg:px-8 pt-16 md:pt-24 pb-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-mono2 text-sky-700 mb-6">
              <Waves className="h-3.5 w-3.5" /> Prepared privately for Gene Lawless · Biolab Australia / Bioguard
            </span>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.03]">
              The operating system for the<br className="hidden md:block" /> modern <span className="aqua-text">pool &amp; spa</span> business.
            </h1>
            <p className="mt-5 max-w-2xl text-lg md:text-xl text-slate-600">
              Shop. Field. Backyard. Aqualux Nexus turns a single water test into products, sales, service and
              self-dosing automation — from the shop counter to the customer's pool — all on one live database
              built from <span className="font-semibold text-slate-900">15 years of real trade</span>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#invite" data-testid="proposal-hero-cta" className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white font-semibold px-6 py-3 transition-colors">
                The partnership <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#software" className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-6 py-3 transition-colors">
                See the software
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-3">
            {STATS.map((s) => (
              <div key={s.l} className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-center">
                <p className="font-display text-2xl font-extrabold text-slate-900">{s.n}</p>
                <p className="text-[11px] text-slate-500 mt-1 leading-tight">{s.l}</p>
              </div>
            ))}
          </motion.div>
          <p className="mt-3 text-xs text-slate-400">Real records migrated from the live business — not a demo.</p>
        </div>
      </section>

      {/* Opportunity */}
      <Section>
        <Eyebrow>The opportunity</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 max-w-3xl">A worldwide, multi-billion-dollar industry — still run on guesswork.</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[
            ["Water care is still confusing", "For shops, technicians and owners alike — manual tests, inconsistent advice, missed sales and unhappy customers."],
            ["Everyone's shipping \u201CAI pool apps\u201D", "A wave of look-alike software launched this year. Almost none own the trade data, the hardware, and the home-automation path together."],
            ["The moat is the whole loop", "Test \u2192 product \u2192 sale \u2192 service \u2192 auto-dose \u2192 reorder, on one database. That closed loop is what nobody else has — and what we've already built the core of."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-display text-lg font-bold">{t}</h3>
              <p className="mt-2 text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Ecosystem */}
      <Section dark id="ecosystem">
        <Eyebrow>One platform · four products</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-white">Everything a pool business touches — connected.</h2>
        <p className="mt-3 text-sky-100/80 max-w-2xl">The shop tester, the mobile field unit, the home automation system and the software that ties them together. One customer record follows the water everywhere.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRODUCTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                <img src={p.img} alt={p.name} className="w-full h-40 object-cover" />
                <div className="p-5">
                  <div className="flex items-center gap-2 text-sky-300"><Icon className="h-4 w-4" /><span className="font-mono2 text-[10px] uppercase tracking-widest">{p.tag}</span></div>
                  <h3 className="font-display text-lg font-bold mt-1.5 text-white">{p.name}</h3>
                  <p className="mt-1.5 text-sm text-sky-100/70 leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* Software today */}
      <Section id="software">
        <Eyebrow>The software · live today</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 max-w-3xl">A water test becomes a sale in one tap.</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">This is the real, running system — the engine of the whole platform. It's designed so staff <em>recognise, don't recall</em>: big buttons, colour-coded, nothing to memorise.</p>

        <div className="mt-10">
          <Shot src="/proposal/ns-switchboard.png" tag="Aqualux Nexus · Switchboard" cap="The Switchboard — the whole business on one screen. Big, colour-coded tiles: recognise, don't recall. Nothing to memorise." />
        </div>
        <div className="mt-6 grid lg:grid-cols-2 gap-6 items-start">
          <Shot src="/proposal/ns-water-test.png" tag="Aqualux Nexus · Water Test" cap="Guided testing against each pool's own ranges — every reading auto-judged “inside value”, then one button: Sell Recommended at POS." />
          <Shot src="/proposal/ns-client.png" tag="Aqualux Nexus · Client cockpit" cap="Every customer's full history — quotes, invoices, work orders, contracts and water tests — on one screen." />
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FLOW.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.t} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center"><Icon className="h-4 w-4 text-sky-600" /></span>
                  <span className="font-mono2 text-[11px] text-slate-400">STEP {i + 1}</span>
                </div>
                <h3 className="font-display font-bold mt-2">{f.t}</h3>
                <p className="mt-1 text-sm text-slate-600">{f.d}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-8 grid lg:grid-cols-2 gap-6 items-start">
          <Shot src="/proposal/ns-pos.png" tag="Aqualux Nexus · Point of Sale" cap="A touch-first POS — over-counter sales, client invoice payments and loyalty, all one tap from the water test." />
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-6 self-center">
            <ShoppingCart className="h-7 w-7 text-sky-600" />
            <p className="mt-3 font-display text-lg font-bold">Every sale is a loyal, repeat sale.</p>
            <p className="mt-2 text-sm text-slate-600">Loyalty tiers, split payments and refunds are built in — and because the whole history lives on the customer record, the next visit's advice is already smarter.</p>
          </div>
        </div>
      </Section>

      {/* Proven foundation */}
      <Section className="bg-slate-50">
        <Eyebrow>Not a wishlist — a rebuild</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 max-w-3xl">Built on 15 years of a real, battle-tested business.</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">
          For over a decade this business ran on a deep, custom desktop system — proven every single day, but locked to one machine.
          Aqualux Nexus is that entire system reborn: same hard-won depth, now cloud-based, connected and ready to scale.
        </p>
        <div className="mt-10 grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            <Shot src="/proposal/legacy-access.png" tag="Before · the original desktop system" cap="15 years of real invoicing, jobs and customer history." />
            <Shot src="/proposal/client-purchases.png" tag="Before · one customer's buying history" cap="Deep purchase records — the data that makes recommendations smart." />
          </div>
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-6">
            <ShieldCheck className="h-7 w-7 text-sky-600" />
            <p className="mt-3 font-display text-lg font-bold">The data is the moat.</p>
            <p className="mt-2 text-sm text-slate-600">
              34,000+ invoices, 51,000+ quotes, 38,000+ work orders, 6,530 pools and 8,897 customers are already migrated and live.
              New entrants start from zero. We start from fifteen years.
            </p>
          </div>
        </div>
      </Section>

      {/* Features & roadmap */}
      <Section>
        <Eyebrow>Scope</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Outstanding functionality.</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-8">
          <div>
            <p className="font-display font-bold text-slate-900 flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Live in the software now</p>
            <ul className="mt-4 space-y-2.5">
              {LIVE.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /><span className="text-sm">{f}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-display font-bold text-slate-900 flex items-center gap-2"><Circle className="h-5 w-5 text-sky-500" /> On the roadmap</p>
            <ul className="mt-4 space-y-2.5">
              {NEXT.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-slate-700"><Circle className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" /><span className="text-sm">{f}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Hardware teasers */}
      <Section dark>
        <Eyebrow>The hardware · a glimpse</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-white">Designed, drawn and specified — ready to build.</h2>
        <p className="mt-3 text-sky-100/80 max-w-2xl">A reagentless tester and a home test-and-dose unit are fully engineered. Detailed drawings, the component schedule and the manufacturing brief are held under NDA and shared on request.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            [TESTER_IMG.front, "Pool Shop bench tester"],
            [MOBILE_IMG, "Mobile on-site technician tester"],
            [IMG.scene, "Home test-and-dose system"],
            [IMG.controller, "Smart dose controller"],
            [IMG.dosing, "Plug-and-play doser modules"],
            [IMG.moduleFamily, "The full module family"],
          ].map(([img, cap]) => (
            <figure key={cap} className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
              <img src={img} alt={cap} className="w-full h-52 object-cover" />
              <figcaption className="px-4 py-3 text-sm text-sky-100/80">{cap}</figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-6 flex items-center gap-2 text-xs text-sky-200/60"><Layers className="h-4 w-4" /> Full engineering pack available under NDA — kept out of this preview by design.</p>
      </Section>

      {/* Why Bioguard */}
      <Section className="bg-slate-50">
        <Eyebrow>Why Biolab / Bioguard</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 max-w-3xl">Your chemistry, at the centre of the loop.</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {[
            ["Bioguard science, everywhere", "The tester recommends Bioguard. The home unit doses Bioguard. The reorder is Bioguard. Every backyard becomes a loyal, automatic Bioguard account."],
            ["A live consumer database", "Real-time water history and dosing data across thousands of pools — brand insight and a direct channel to owners that no competitor can match."],
            ["Hardware + software + data", "Others have one piece. Together we own the tester, the software, the automation and the trade data — the full closed loop."],
            ["A proven operator, not a pitch deck", "Built by someone who ran the business for 15 years and lived in the software daily. The product is real and already running."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-display text-lg font-bold">{t}</h3>
              <p className="mt-2 text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* The ask */}
      <Section>
        <Eyebrow>The ask</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 max-w-3xl">A full strategic alliance.</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">I'm inviting Bioguard to come in as the cornerstone partner across all four fronts. Commercial figures and structure to be shaped together on discussion.</p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ASK.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.t} className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className="h-10 w-10 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center"><Icon className="h-5 w-5 text-sky-600" /></span>
                <h3 className="font-display font-bold mt-3">{a.t}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{a.d}</p>
              </div>
            );
          })}
        </div>

        {/* Roadmap */}
        <div className="mt-12 rounded-3xl border border-slate-200 bg-slate-50 p-6 md:p-8">
          <p className="font-display font-bold text-slate-900">The build path</p>
          <div className="mt-5 grid md:grid-cols-3 gap-5">
            {ROADMAP.map((r) => (
              <div key={r.step} className="rounded-2xl bg-white border border-slate-200 p-5">
                <span className="font-mono2 text-xs text-sky-600">STEP {r.step}</span>
                <h4 className="font-display font-bold mt-1">{r.name}</h4>
                <p className="mt-1.5 text-sm text-slate-600">{r.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-sm text-slate-500">{COMMERCIAL.rollout}</p>
        </div>
      </Section>

      {/* Invite Gene */}
      <Section dark id="invite">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <Eyebrow>Let's talk, Gene</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-white">Twenty minutes could start something big.</h2>
            <p className="mt-4 text-sky-100/80">
              I only get one shot at showing you this, so thank you for reading. If the vision resonates, I'd love a short
              call to walk you through the live software and where Bioguard fits. Everything here is under the confidentiality
              agreement you accepted on entry.
            </p>
            <div className="mt-6 space-y-2 text-sky-100">
              <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-sky-300" /> {CONTACT.name} · {CONTACT.company}</p>
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-2 hover:text-white"><Phone className="h-4 w-4 text-sky-300" /> {CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="h-4 w-4 text-sky-300" /> {CONTACT.email}</a>
              <p className="text-sky-100/70 text-sm pt-1">{CONTACT.location}</p>
            </div>
          </div>

          <div className="rounded-2xl bg-white text-slate-900 p-5 md:p-6">
            <p className="font-display font-bold flex items-center gap-2"><Mail className="h-4 w-4 text-sky-600" /> Send Gene the invitation</p>
            <p className="text-xs text-slate-500 mt-1">Pre-written and personalised, with the private proposal link included. Open it in your email app, or copy it.</p>
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 max-h-56 overflow-auto">
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">To</p>
              <p className="text-sm text-slate-800 font-medium">gene.lawless@biolabinc.com</p>
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mt-3">Subject</p>
              <p className="text-sm text-slate-800 font-medium">{OUTREACH.biolab.subject}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mt-3">Body</p>
              <pre className="whitespace-pre-wrap font-sans text-[13px] text-slate-700 leading-relaxed mt-1" data-testid="invite-body">{inviteBody}</pre>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={mailto} data-testid="proposal-invite-mailto" className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                <Mail className="h-4 w-4" /> Open in email app
              </a>
              <button onClick={copyInvite} data-testid="proposal-invite-copy" className={`inline-flex items-center gap-2 rounded-full text-sm font-semibold px-5 py-2.5 transition-colors ${copied ? "bg-emerald-600 text-white" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="px-5 lg:px-8 py-10 border-t border-slate-200">
        <div className="mx-auto max-w-6xl flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
          <span className="flex items-center gap-2"><Beaker className="h-4 w-4" /> Aqualux Nexus · Confidential — prepared for Biolab Australia. Figures indicative, subject to discussion &amp; engineering validation.</span>
          <Link to="/" className="hover:text-slate-600">Back to hub</Link>
        </div>
      </footer>
    </div>
  );
}
